'use client';

import { useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Heading, Modal } from '@zillow/constellation-10';
import type { ContactSubmissionContext } from './types';
import { postContactFlowConfig } from './postContactFlowConfig';
import { getActivePostContactFlows, getPostContactFlowTitleKey } from './postContactFlowRegistry';
import { emitTrackPostContactPopupViewEvent } from '@/src/analytics/hotpads/events';
import * as styles from './postContactFlow.styles';
import { useModalDismissProps } from '@/src/lib/hooks/ui/useModalDismissProps';

type PostContactFlowModalProps = {
  context: ContactSubmissionContext | null;
  onDone: () => void;
};

export function PostContactFlowModal({ context, onDone }: PostContactFlowModalProps) {
  const modalDismissProps = useModalDismissProps();
  const t = useTranslations('contactForm');
  const [stepIndex, setStepIndex] = useState(0);
  const activeFlows = useMemo(() => getActivePostContactFlows(context), [context]);
  const currentFlow = activeFlows[stepIndex] ?? null;
  const currentConfig = currentFlow ? postContactFlowConfig.find((flow) => flow.type === currentFlow.type) : null;
  const CurrentStep = currentConfig?.component ?? null;

  useEffect(() => {
    setStepIndex(0);
    if (context) {
      emitTrackPostContactPopupViewEvent();
    }
  }, [context]);

  const handleNext = () => {
    if (stepIndex < activeFlows.length - 1) {
      setStepIndex((prev) => prev + 1);
    } else {
      onDone();
    }
  };

  const title = context && currentFlow ? t(getPostContactFlowTitleKey(currentFlow, context)) : t('successTitleContact');

  return (
    <Modal.Root
      useDismissProps={modalDismissProps}
      open={Boolean(context)}
      onOpenChange={(open) => !open && onDone()}
      size={{ base: 'fullScreen', lg: 'md' }}
    >
      <Modal.Portal>
        <Modal.Backdrop>
          <Modal.Content data-testid="hdp-post-contact-flow">
            <Modal.Header>
              <Heading level={2}>{title}</Heading>
            </Modal.Header>
            <Modal.Body>
              <div className={styles.modalBody}>
                {context && CurrentStep && <CurrentStep context={context} onComplete={handleNext} />}
              </div>
            </Modal.Body>
            <Modal.CloseButton />
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
}
